import { featureCollection, type LineString } from '@turf/helpers';
import { useMemo } from 'react';
import { LineProperties } from '@alltrails/types/CustomRoute';
import { useAppSelector } from '../../../redux';
import { customRouteInactivePolylinesUniqueId } from '../../../utils/layers';
import { PolylineSource } from '../../Polyline';
import getLineStrings from './getLineStrings';

// Use a separate source for inactive segments so that updates to the active segment do not cause a re-render of the inactive segments.
// https://docs.mapbox.com/help/troubleshooting/mapbox-gl-js-performance/#use-a-separate-geojson-source-for-data-that-needs-to-be-updated-rapidly

export const UNIQUE_ID = customRouteInactivePolylinesUniqueId;

type InactivePolylinesProps = {
  hideArrows?: boolean;
};

const InactivePolylines = ({ hideArrows }: InactivePolylinesProps) => {
  const { activeSegmentIndex, hoveredSegmentIndex, selectedWaypointId, segments } = useAppSelector(state => ({
    activeSegmentIndex: state.customRoute.activeSegmentIndex,
    hoveredSegmentIndex: state.customRoute.hoveredSegmentIndex,
    selectedWaypointId: state.customRoute.selectedWaypointId,
    segments: state.customRoute.route.segments
  }));

  const opacity = selectedWaypointId !== undefined || activeSegmentIndex !== undefined || hoveredSegmentIndex !== undefined ? 0.5 : 1;

  const data = useMemo(
    () =>
      featureCollection<LineString, LineProperties>(
        segments
          ?.map((segment, index) => ({ segment, index }))
          .filter(({ index }) => index !== activeSegmentIndex)
          .map(({ segment, index }) => getLineStrings(segment, index === hoveredSegmentIndex ? 1 : undefined, index))
          .flat()
      ),
    [activeSegmentIndex, hoveredSegmentIndex, segments]
  );

  return <PolylineSource data={data} hideArrows={hideArrows} opacity={opacity} uniqueId={UNIQUE_ID} hasSourcePaint />;
};

export default InactivePolylines;
